import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { MobileBottomNav, NAV_LINK_CLASS, NAV_CTA_CLASS } from '../components/PublicNav';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Users, Zap, Trophy, HelpCircle, Bell, Settings,
  Check, ArrowRight, Menu, X, LogIn,
  Flame, Play, BarChart3, Calendar, Sparkles,
  ChevronDown, FileText, BookOpen, Users as UsersIcon
} from 'lucide-react';

// Feature icons mapping
const FeatureIcon = ({ name, className }) => {
  const icons = {
    league: Users,
    draft: Zap,
    scoring: Trophy,
    bonus: HelpCircle,
    leaderboard: BarChart3,
    notifications: Bell,
    admin: Settings,
    seasons: Calendar
  };
  const Icon = icons[name] || Sparkles;
  return <Icon className={className} />;
};

// Feature screenshot image component with click to enlarge
const FeatureImage = ({ src, alt, className = "", onImageClick }) => (
  <div 
    className={`aspect-video rounded-xl overflow-hidden shadow-lg border border-[#1E3A4C]/10 cursor-pointer group relative ${className}`}
    onClick={() => onImageClick && onImageClick(src, alt)}
  >
    <img 
      src={src} 
      alt={alt} 
      className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
      loading="lazy"
    />
    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-2 shadow-lg">
        <svg className="w-5 h-5 text-[#1E3A4C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
        </svg>
      </div>
    </div>
  </div>
);

// Image lightbox modal component
const ImageLightbox = ({ isOpen, imageSrc, imageAlt, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <button 
        className="absolute top-4 right-4 text-white hover:text-[#FF7F50] transition-colors z-10"
        onClick={onClose}
        aria-label="Close lightbox"
      >
        <X className="w-8 h-8" />
      </button>
      <div 
        className="relative max-w-6xl max-h-[90vh] w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <img 
          src={imageSrc} 
          alt={imageAlt}
          className="w-full h-full object-contain rounded-lg shadow-2xl"
        />
        <p className="text-white/80 text-center mt-3 text-sm">{imageAlt}</p>
      </div>
    </div>
  );
};

// Section divider component
const SectionDivider = ({ variant = "wave" }) => (
  <div className="relative h-16 md:h-24 overflow-hidden">
    {variant === "wave" && (
      <svg className="absolute bottom-0 w-full h-full" viewBox="0 0 1440 120" preserveAspectRatio="none">
        <path 
          fill="rgba(255, 127, 80, 0.1)" 
          d="M0,64L48,69.3C96,75,192,85,288,80C384,75,480,53,576,48C672,43,768,53,864,69.3C960,85,1056,107,1152,101.3C1248,96,1344,64,1392,48L1440,32L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"
        />
      </svg>
    )}
    {variant === "sand" && (
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#F5F0E6]/50 to-[#F5F0E6]" />
    )}
  </div>
);

export default function HowItWorksPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [showStickyNav, setShowStickyNav] = useState(false);
  const [lightboxImage, setLightboxImage] = useState({ isOpen: false, src: '', alt: '' });

  // Handle image click to open lightbox
  const handleImageClick = (src, alt) => {
    setLightboxImage({ isOpen: true, src, alt });
    document.body.style.overflow = 'hidden'; // Prevent background scroll
  };

  // Handle lightbox close
  const handleLightboxClose = () => {
    setLightboxImage({ isOpen: false, src: '', alt: '' });
    document.body.style.overflow = ''; // Restore scroll
  };

  // Scroll spy for active section and sticky nav visibility
  useEffect(() => {
    const handleScroll = () => {
      // Show sticky sub-nav after scrolling past hero (about 400px)
      setShowStickyNav(window.scrollY > 400);
      
      const sections = ['features', 'how-it-works', 'screenshots', 'faq'];
      for (const section of sections) {
        const el = document.getElementById(section);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      // Offset for the sticky nav
      const offset = 80;
      const elementPosition = el.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const sectionNavLinks = [
    { label: 'Features', id: 'features' },
    { label: 'How it Works', id: 'how-it-works' },
    { label: 'FAQ', id: 'faq' },
  ];

  return (
    <div className="min-h-screen pb-24 md:pb-0" style={{ backgroundColor: '#F5F0E6' }}>
      <Helmet>
        <title>How It Works - Survivor Fantasy League | Rules, Scoring & Features</title>
        <meta name="description" content="Learn how to play Survivor Fantasy League! Discover the draft process, scoring rules, bonus questions, and league management features. Step-by-step guide for new players." />
        <link rel="canonical" href="https://survivorfantasy.live/how-it-works" />
        <meta property="og:title" content="How It Works - Survivor Fantasy League" />
        <meta property="og:description" content="Learn how to play Survivor Fantasy League! Draft contestants, earn points from episode events, and compete with friends." />
        <meta property="og:url" content="https://survivorfantasy.live/how-it-works" />
      </Helmet>
      
      {/* Top announcement bar - hidden until Season 51 air date is announced */}

      {/* Main Navigation - EXACTLY matching PublicLandingPage */}
      <nav className="relative z-20 container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 lg:gap-3 min-w-0 flex-1">
            <img 
              src="/logo-new.png" 
              alt="Survivor Fantasy League" 
              className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 object-contain flex-shrink-0"
            />
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg lg:text-2xl font-bold text-[#1E3A4C] truncate" 
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Survivor Fantasy League
              </h1>
              <p className="text-[#FF7F50] text-[11px] sm:text-xs lg:text-sm font-medium whitespace-nowrap">Outwit • Outplay • Outlast</p>
            </div>
          </Link>
          
          <div className="flex items-center gap-1 lg:gap-3 flex-shrink-0">
            <Link to="/">
              <Button variant="ghost" className={`hidden md:flex ${NAV_LINK_CLASS}`}>
                Home
              </Button>
            </Link>

            {/* Learn More Dropdown - desktop */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className={`hidden md:flex ${NAV_LINK_CLASS}`} data-testid="nav-learn-more-trigger">
                  Learn More
                  <ChevronDown className="w-4 h-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/rules" className="flex items-center gap-2 cursor-pointer" data-testid="nav-rules">
                    <FileText className="w-4 h-4" />
                    Rules & Scoring
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/faq" className="flex items-center gap-2 cursor-pointer" data-testid="nav-faq">
                    <HelpCircle className="w-4 h-4" />
                    FAQ
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/blog" className="flex items-center gap-2 cursor-pointer" data-testid="nav-blog">
                    <BookOpen className="w-4 h-4" />
                    Blog
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/about" className="flex items-center gap-2 cursor-pointer" data-testid="nav-about">
                    <UsersIcon className="w-4 h-4" />
                    About
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/login?tab=register">
              <Button variant="ghost" className={`hidden md:flex ${NAV_LINK_CLASS}`}>
                Sign Up
              </Button>
            </Link>
            <Link to="/login">
              <Button className={NAV_CTA_CLASS} style={{ backgroundColor: '#FF7F50' }}>
                <LogIn className="w-4 h-4 mr-1 lg:mr-2" />
                Log In
              </Button>
            </Link>
            
            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2 text-[#1E3A4C]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        
        {/* Mobile Nav Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-sm rounded-lg mt-4 py-4 px-4 shadow-lg">
            <nav className="flex flex-col gap-2">
              <Link to="/" className="py-2 text-[#1E3A4C] font-medium">Home</Link>
              <div className="border-t border-[#1E3A4C]/10 my-2 pt-2">
                <p className="text-xs text-[#1E3A4C]/50 uppercase tracking-wide mb-2">On This Page</p>
                {sectionNavLinks.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className="block w-full text-left py-2 text-[#1E3A4C] hover:text-[#FF7F50] font-medium"
                  >
                    {link.label}
                  </button>
                ))}
              </div>
              <Link to="/login?tab=register">
                <Button className="w-full mt-2 text-white" style={{ backgroundColor: '#FF7F50' }}>
                  Sign Up Free
                </Button>
              </Link>
            </nav>
          </div>
        )}
      </nav>

      {/* Sticky Section Sub-Navigation - Separate from main header, appears on scroll */}
      <div className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${showStickyNav ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}`}>
        <div className="bg-[#1E3A4C] shadow-lg">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between h-12">
              {/* Section nav links */}
              <nav className="flex items-center gap-1 md:gap-6 overflow-x-auto scrollbar-hide">
                {sectionNavLinks.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className={`text-sm font-medium transition-colors whitespace-nowrap px-2 py-1 rounded ${
                      activeSection === link.id 
                        ? 'text-[#FF7F50] bg-white/10' 
                        : 'text-[#F5F0E6]/80 hover:text-white'
                    }`}
                  >
                    {link.label}
                  </button>
                ))}
              </nav>
              
              {/* CTA */}
              <Link to="/login?tab=register" className="flex-shrink-0 ml-4">
                <Button size="sm" className="text-[#1E3A4C] bg-[#F5F0E6] hover:bg-white text-xs md:text-sm">
                  Sign Up Free
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden" style={{ backgroundColor: '#F5F0E6' }}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `url("/sfl-bg.png")`,
          backgroundSize: '400px 400px',
          backgroundRepeat: 'repeat'
        }}></div>
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 md:py-24">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-center md:text-left">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight" style={{ fontFamily: 'Playfair Display, serif', color: '#1E3A4C' }}>
                Draft. Predict.<br />
                <span style={{ color: '#FF7F50' }}>Talk trash.</span><br />
                Repeat.
              </h1>
              
              <p className="mt-6 text-lg max-w-xl" style={{ color: '#4682B4' }}>
                Survivor Fantasy League makes it easy to run a season with friends. Set up a league, draft your castaways, earn points weekly, and watch the leaderboard swing like a coconut in a storm.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <Button 
                  size="lg"
                  onClick={() => navigate('/login?tab=register')}
                  className="text-white text-lg px-8"
                  style={{ backgroundColor: '#FF7F50' }}
                >
                  Sign Up Free
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => scrollToSection('how-it-works')}
                  className="bg-white/80 hover:bg-white border-2"
                  style={{ borderColor: '#1E3A4C', color: '#1E3A4C' }}
                >
                  <Play className="w-5 h-5 mr-2" />
                  See How It Works
                </Button>
              </div>

              <p className="mt-6 text-sm" style={{ color: '#4682B4' }}>
                Free to play. Built for <strong>Survivor 50 and beyond</strong>. Early adopters get bonus karma and the right to complain directly to the builder.
              </p>
            </div>

            <div className="relative">
              <img
                src="/hero-roster-mockup.png"
                alt="The Survivor Fantasy League roster screen on a phone, showing drafted castaways with their points, tribes and elimination status"
                width="1024"
                height="1024"
                loading="eager"
                className="w-full max-w-md mx-auto drop-shadow-2xl relative z-10"
                data-testid="how-it-works-hero-image"
              />
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full blur-2xl" style={{ backgroundColor: 'rgba(255, 127, 80, 0.3)' }} />
              <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full blur-3xl" style={{ backgroundColor: 'rgba(70, 130, 180, 0.3)' }} />
            </div>
          </div>
        </div>
      </section>

      {/* Early Access Callout - Enhanced Visual Design */}
      <section className="py-16 md:py-24 relative overflow-hidden" style={{ backgroundColor: '#1E3A4C' }}>
        {/* Decorative background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-64 h-64 rounded-full blur-3xl opacity-20" style={{ backgroundColor: '#FF7F50' }} />
          <div className="absolute bottom-10 right-10 w-72 h-72 rounded-full blur-3xl opacity-15" style={{ backgroundColor: '#4682B4' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl opacity-10" style={{ backgroundColor: '#FF7F50' }} />
        </div>
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-6 py-2 bg-[#FF7F50]/20 text-lg font-bold rounded-full mb-6" style={{ color: '#FF7F50' }}>
              <Zap className="w-5 h-5" />
              Early Access Energy
              <Zap className="w-5 h-5" />
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              You're Getting In Early
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#F5F0E6' }}>
              This is early access, which means you're part of shaping what this becomes.
            </p>
          </div>
          
          {/* Feature cards */}
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-[#FF7F50]/50 transition-all hover:transform hover:-translate-y-1">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: '#FF7F50' }}>
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Newest Features First</h3>
              <p style={{ color: '#F5F0E6' }}>You get to try new stuff before anyone else. Some of it might even work.</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-[#FF7F50]/50 transition-all hover:transform hover:-translate-y-1">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: '#4682B4' }}>
                <Users className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Your Voice Matters</h3>
              <p style={{ color: '#F5F0E6' }}>Your feedback actually shapes the app. We're listening (and probably taking notes).</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-[#FF7F50]/50 transition-all hover:transform hover:-translate-y-1">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: '#1E3A4C', border: '2px solid #FF7F50' }}>
                <Flame className="w-7 h-7" style={{ color: '#FF7F50' }} />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Island Vibes Only</h3>
              <p style={{ color: '#F5F0E6' }}>If something looks weird, it's the island. We're building in the jungle here.</p>
            </div>
          </div>
          
          <div className="text-center mt-10">
            <p style={{ color: '#4682B4' }}>
              Send feedback anytime: <a href="mailto:support@survivorfantasy.live" className="font-medium underline hover:opacity-80" style={{ color: '#FF7F50' }}>support@survivorfantasy.live</a>
            </p>
          </div>
        </div>
      </section>

      <SectionDivider variant="wave" />

      {/* Features Section */}
      <section id="features" className="py-16 md:py-24 relative" style={{ backgroundColor: '#F5F0E6' }}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
          backgroundImage: `url("/sfl-bg.png")`,
          backgroundSize: '400px 400px',
          backgroundRepeat: 'repeat'
        }}></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: '#1E3A4C' }}>
              What You Can Do
            </h2>
            <p className="mt-4 text-lg max-w-2xl mx-auto" style={{ color: '#4682B4' }}>
              Everything you need to run a Survivor fantasy league that your friends will actually stick with.
            </p>
          </div>

          <div className="space-y-16">
            {/* Feature 1 */}
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4" style={{ backgroundColor: '#FF7F50' }}>
                  <FeatureIcon name="league" className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold" style={{ color: '#1E3A4C' }}>Start a league in minutes</h3>
                <p className="mt-3" style={{ color: '#4682B4' }}>
                  Create your league, invite friends and family, and keep the group together season after season. One person runs setup, everyone else just shows up and competes.
                </p>
                <ul className="mt-4 space-y-2">
                  {['Create a league and share invites', 'Players join with a simple sign up', 'Keep the league running across seasons'].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#FF7F50' }} />
                      <span style={{ color: '#4682B4' }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <FeatureImage 
                src="https://customer-assets.emergentagent.com/job_3e7f5fbc-42a9-472e-a885-e66668e86141/artifacts/6mg532cy_Create%20League.png" 
                alt="Create League interface showing league name input and admin features"
                onImageClick={handleImageClick}
              />
            </div>

            {/* Feature 2 */}
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div className="order-2 md:order-1">
                <FeatureImage 
                  src="https://customer-assets.emergentagent.com/job_3e7f5fbc-42a9-472e-a885-e66668e86141/artifacts/z0whgke6_Draft.png" 
                  alt="Draft interface showing contestant cards with player info"
                  onImageClick={handleImageClick}
                />
              </div>
              <div className="order-1 md:order-2">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4" style={{ backgroundColor: '#4682B4' }}>
                  <FeatureIcon name="draft" className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold" style={{ color: '#1E3A4C' }}>Live draft or remote-friendly auto draft</h3>
                <p className="mt-3" style={{ color: '#4682B4' }}>
                  Draft together in real time, or run a ranked-choice style auto draft when schedules are impossible.
                </p>
                <ul className="mt-4 space-y-2">
                  {[
                    { text: 'Snake draft', sub: '(classic, dramatic)' },
                    { text: 'Random order', sub: '(maximum chaos)' },
                    { text: 'Custom order', sub: '(when your group argues about fairness)' },
                    { text: 'Auto draft (ranked choice)', sub: 'for remote drafts' },
                    { text: 'Optional pick timer and double drafting', sub: 'for bigger leagues' }
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#FF7F50' }} />
                      <span style={{ color: '#4682B4' }}><strong>{item.text}</strong> {item.sub}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4" style={{ backgroundColor: '#1E3A4C' }}>
                  <FeatureIcon name="scoring" className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold" style={{ color: '#1E3A4C' }}>Default scoring, or go fully custom</h3>
                <p className="mt-3" style={{ color: '#4682B4' }}>
                  Use default scoring rules or adjust points to match how your group watches the show. Serious league? Silly league? Chaos league? Your call.
                </p>
                <ul className="mt-4 space-y-2">
                  {['Start with a default ruleset', 'Edit point values anytime for future events', 'Keep past scoring consistent in the event log'].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#FF7F50' }} />
                      <span style={{ color: '#4682B4' }}>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-sm italic" style={{ color: '#4682B4' }}>
                  (League Admins still need to manually score the events each week.)
                </p>
              </div>
              <FeatureImage 
                src="https://customer-assets.emergentagent.com/job_3e7f5fbc-42a9-472e-a885-e66668e86141/artifacts/wykho9ni_Scoring.png" 
                alt="Scoring Rules interface showing event names and point values"
                onImageClick={handleImageClick}
              />
            </div>

            {/* Feature 4 */}
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div className="order-2 md:order-1">
                <FeatureImage 
                  src="https://customer-assets.emergentagent.com/job_3e7f5fbc-42a9-472e-a885-e66668e86141/artifacts/d5wc5w03_Bonus%20Question.png" 
                  alt="Weekly Bonus Questions management interface with graded and open questions"
                  onImageClick={handleImageClick}
                />
              </div>
              <div className="order-1 md:order-2">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4" style={{ backgroundColor: '#FF7F50' }}>
                  <FeatureIcon name="bonus" className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold" style={{ color: '#1E3A4C' }}>Bonus questions that keep everyone engaged</h3>
                <p className="mt-3" style={{ color: '#4682B4' }}>
                  Add weekly predictions for extra points. Use AI suggestions to speed things up, then approve and tweak before posting.
                </p>
                <ul className="mt-4 space-y-2">
                  {['Create bonus questions episode by episode', 'Optional AI-generated drafts to save time', 'Extra points keeps the middle of the pack fighting'].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#FF7F50' }} />
                      <span style={{ color: '#4682B4' }}>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-sm italic" style={{ color: '#4682B4' }}>
                  (No spoilers, just predictions. Good clean fun.)
                </p>
              </div>
            </div>

            {/* Feature 5 */}
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4" style={{ backgroundColor: '#4682B4' }}>
                  <FeatureIcon name="leaderboard" className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold" style={{ color: '#1E3A4C' }}>Track the chaos week over week</h3>
                <p className="mt-3" style={{ color: '#4682B4' }}>
                  See standings shift as episodes get scored. The leaderboard makes it painfully clear who is thriving and who is guessing vibes.
                </p>
                <ul className="mt-4 space-y-2">
                  {['Current rankings and totals', 'Trend line over time (momentum matters)', 'Great for friendly trash talk'].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#FF7F50' }} />
                      <span style={{ color: '#4682B4' }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <FeatureImage 
                src="https://customer-assets.emergentagent.com/job_3e7f5fbc-42a9-472e-a885-e66668e86141/artifacts/l5jmxgxk_Leaderboard.png" 
                alt="Leaderboard showing points progression chart and player rankings"
                onImageClick={handleImageClick}
              />
            </div>

            {/* Feature 6 */}
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div className="order-2 md:order-1">
                <FeatureImage 
                  src="https://customer-assets.emergentagent.com/job_3e7f5fbc-42a9-472e-a885-e66668e86141/artifacts/cwg9kdhr_Notifications.png" 
                  alt="Notification Preferences showing email and SMS toggles"
                  onImageClick={handleImageClick}
                />
              </div>
              <div className="order-1 md:order-2">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4" style={{ backgroundColor: '#1E3A4C' }}>
                  <FeatureIcon name="notifications" className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold" style={{ color: '#1E3A4C' }}>Nudges for drafts, reminders, and scoring</h3>
                <p className="mt-3" style={{ color: '#4682B4' }}>
                  Email, SMS, and in-app notifications help your league stay on track, especially on draft night.
                </p>
                <ul className="mt-4 space-y-2">
                  {['"It\'s your turn" draft alerts', 'Reminders so no one forgets to participate', 'Updates when the episode is scored'].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#FF7F50' }} />
                      <span style={{ color: '#4682B4' }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Feature 7 */}
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4" style={{ backgroundColor: '#FF7F50' }}>
                  <FeatureIcon name="admin" className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold" style={{ color: '#1E3A4C' }}>A clean command center for League Admins</h3>
                <p className="mt-3" style={{ color: '#4682B4' }}>
                  Run seasons, manage contestants, scoring, bonus questions, and recalculations from one place.
                </p>
                <ul className="mt-4 space-y-2">
                  {['Season settings and status', 'Contestants + roster management', 'Scoring + event log controls', 'Quick actions for common tasks'].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#FF7F50' }} />
                      <span style={{ color: '#4682B4' }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <FeatureImage 
                src="https://customer-assets.emergentagent.com/job_3e7f5fbc-42a9-472e-a885-e66668e86141/artifacts/1ldzkwvq_Admin%20Dash.png" 
                alt="Admin Dashboard with season management, quick actions, and feature cards"
                onImageClick={handleImageClick}
              />
            </div>
          </div>
        </div>
      </section>

      <SectionDivider variant="sand" />

      {/* How It Works Section - Compact Zigzag Timeline */}
      <section id="how-it-works" className="py-12 md:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: '#1E3A4C' }}>
              How It Works
            </h2>
            <p className="mt-1" style={{ color: '#4682B4' }}>(no machete required)</p>
          </div>

          {/* Desktop Timeline - Grid Layout */}
          <div className="hidden md:grid grid-cols-[1fr_auto_1fr] gap-x-4 items-center">
            {/* Row 1 */}
            <div className="rounded-xl border-2 p-3 text-center" style={{ borderColor: '#4682B4' }}>
              <div className="flex items-center gap-2 justify-center">
                <span className="font-semibold" style={{ color: '#1E3A4C' }}>Create an account</span>
                <Users className="w-4 h-4" style={{ color: '#FF7F50' }} />
              </div>
              <p className="text-sm" style={{ color: '#4682B4' }}>Quick signup, no credit card needed.</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-0.5 h-4" style={{ backgroundColor: '#FF7F50' }} />
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: '#FF7F50' }}>1</div>
              <div className="w-0.5 h-4" style={{ backgroundColor: '#FF7F50' }} />
            </div>
            <div></div>

            {/* Row 2 */}
            <div></div>
            <div className="flex flex-col items-center">
              <div className="w-0.5 h-4" style={{ backgroundColor: '#FF7F50' }} />
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: '#FF7F50' }}>2</div>
              <div className="w-0.5 h-4" style={{ backgroundColor: '#FF7F50' }} />
            </div>
            <div className="rounded-xl border-2 p-3 text-center" style={{ borderColor: '#4682B4' }}>
              <div className="flex items-center gap-2 justify-center">
                <Trophy className="w-4 h-4" style={{ color: '#FF7F50' }} />
                <span className="font-semibold" style={{ color: '#1E3A4C' }}>Start or join a league</span>
              </div>
              <p className="text-sm" style={{ color: '#4682B4' }}>Invite friends with a simple link.</p>
            </div>

            {/* Row 3 */}
            <div className="rounded-xl border-2 p-3 text-center" style={{ borderColor: '#4682B4' }}>
              <div className="flex items-center gap-2 justify-center">
                <span className="font-semibold" style={{ color: '#1E3A4C' }}>Create a season</span>
                <Calendar className="w-4 h-4" style={{ color: '#FF7F50' }} />
              </div>
              <p className="text-sm" style={{ color: '#4682B4' }}>Import contestants or add your own.</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-0.5 h-4" style={{ backgroundColor: '#FF7F50' }} />
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: '#FF7F50' }}>3</div>
              <div className="w-0.5 h-4" style={{ backgroundColor: '#FF7F50' }} />
            </div>
            <div></div>

            {/* Row 4 */}
            <div></div>
            <div className="flex flex-col items-center">
              <div className="w-0.5 h-4" style={{ backgroundColor: '#FF7F50' }} />
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: '#FF7F50' }}>4</div>
              <div className="w-0.5 h-4" style={{ backgroundColor: '#FF7F50' }} />
            </div>
            <div className="rounded-xl border-2 p-3 text-center" style={{ borderColor: '#4682B4' }}>
              <div className="flex items-center gap-2 justify-center">
                <Zap className="w-4 h-4" style={{ color: '#FF7F50' }} />
                <span className="font-semibold" style={{ color: '#1E3A4C' }}>Draft your castaways</span>
              </div>
              <p className="text-sm" style={{ color: '#4682B4' }}>Live draft or remote auto-draft.</p>
            </div>

            {/* Row 5 */}
            <div className="rounded-xl border-2 p-3 text-center" style={{ borderColor: '#4682B4' }}>
              <div className="flex items-center gap-2 justify-center">
                <span className="font-semibold" style={{ color: '#1E3A4C' }}>Play weekly</span>
                <BarChart3 className="w-4 h-4" style={{ color: '#FF7F50' }} />
              </div>
              <p className="text-sm" style={{ color: '#4682B4' }}>Bonus questions, scores, leaderboard.</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-0.5 h-4" style={{ backgroundColor: '#FF7F50' }} />
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: '#FF7F50' }}>5</div>
              <div className="w-0.5 h-4" style={{ backgroundColor: '#FF7F50' }} />
            </div>
            <div></div>

            {/* Row 6 */}
            <div></div>
            <div className="flex flex-col items-center">
              <div className="w-0.5 h-4" style={{ backgroundColor: '#FF7F50' }} />
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: '#FF7F50' }}>6</div>
              <div className="w-0.5 h-4" style={{ backgroundColor: '#FF7F50' }} />
            </div>
            <div className="rounded-xl border-2 p-3 text-center" style={{ borderColor: '#4682B4' }}>
              <div className="flex items-center gap-2 justify-center">
                <Flame className="w-4 h-4" style={{ color: '#FF7F50' }} />
                <span className="font-semibold" style={{ color: '#1E3A4C' }}>Repeat next season</span>
              </div>
              <p className="text-sm" style={{ color: '#4682B4' }}>Same league, fresh chaos.</p>
            </div>
          </div>

          {/* Mobile Timeline */}
          <div className="md:hidden space-y-3">
            {[
              { step: 1, title: 'Create an account', desc: 'Quick signup, no credit card needed.', icon: Users },
              { step: 2, title: 'Start or join a league', desc: 'Invite friends with a simple link.', icon: Trophy },
              { step: 3, title: 'Create a season', desc: 'Import contestants or add your own.', icon: Calendar },
              { step: 4, title: 'Draft your castaways', desc: 'Live draft or remote auto-draft.', icon: Zap },
              { step: 5, title: 'Play weekly', desc: 'Bonus questions, scores, leaderboard.', icon: BarChart3 },
              { step: 6, title: 'Repeat next season', desc: 'Same league, fresh chaos.', icon: Flame }
            ].map((item) => {
              const IconComponent = item.icon;
              return (
                <div key={item.step} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: '#FF7F50' }}>
                    {item.step}
                  </div>
                  <div className="flex-1 rounded-lg border p-3" style={{ borderColor: '#4682B4' }}>
                    <div className="flex items-center gap-2">
                      <IconComponent className="w-4 h-4" style={{ color: '#FF7F50' }} />
                      <span className="font-semibold text-sm" style={{ color: '#1E3A4C' }}>{item.title}</span>
                    </div>
                    <p className="text-xs mt-1" style={{ color: '#4682B4' }}>{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-8">
            <Button 
              size="lg"
              onClick={() => navigate('/login?tab=register')}
              className="text-white text-lg px-8"
              style={{ backgroundColor: '#FF7F50' }}
            >
              Sign Up Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 md:py-24 relative" style={{ backgroundColor: '#F5F0E6' }}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
          backgroundImage: `url("/sfl-bg.png")`,
          backgroundSize: '400px 400px',
          backgroundRepeat: 'repeat'
        }}></div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: '#1E3A4C' }}>
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-6">
            {[
              { q: 'Is it free?', a: 'Yes. Sign up is free and leagues are free.' },
              { q: 'Do I need to be the League Admin?', a: 'Nope. One person creates the league, everyone else joins and plays.' },
              { q: "Can we draft if we can't all meet at the same time?", a: 'Yes. Use the remote-friendly auto draft where players rank contestants ahead of time.' },
              { q: 'Can we customize scoring?', a: 'Yes. League Admins can use default scoring or customize rules.' },
              { q: 'Are episode events automatically scored?', a: 'No. The League Admin will need to manually input events (either through bulk entry or individually) for each episode, then Finalize and Notify the players in your League.' },
              { q: 'Does this spoil anything?', a: 'No. Bonus questions are predictions only.' },
              { q: 'Can we keep our league going next season?', a: 'Yep. Multi-season support is built in, including multiple countries.' }
            ].map((item, i) => (
              <Card key={i} className="bg-white border-2" style={{ borderColor: '#4682B4' }}>
                <CardContent className="p-6">
                  <h3 className="font-bold flex items-start gap-2" style={{ color: '#1E3A4C' }}>
                    <HelpCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#FF7F50' }} />
                    {item.q}
                  </h3>
                  <p className="mt-2 ml-7" style={{ color: '#4682B4' }}>{item.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA - Matching PublicLandingPage style */}
      <section className="py-16 md:py-24 relative overflow-hidden" style={{ backgroundColor: '#1E3A4C' }}>
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: '#FF7F50' }} />
          <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: '#4682B4', animationDelay: '1s' }} />
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center relative z-10">
          <div className="mb-8">
            <img 
              src="/logo-new.png" 
              alt="Survivor Fantasy League" 
              className="w-32 h-32 mx-auto drop-shadow-2xl"
            />
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to start your league?
          </h2>
          <p className="text-lg mb-10" style={{ color: '#F5F0E6' }}>
            Create a league, invite your crew, and get draft night locked in.
          </p>
          <Button 
            size="lg"
            onClick={() => navigate('/login?tab=register')}
            className="text-white px-10 py-7 text-xl rounded-full shadow-2xl border-2 transform hover:scale-105 transition-all"
            style={{ backgroundColor: '#FF7F50', borderColor: '#FF7F50' }}
          >
            Sign Up Free
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer - Matching PublicLandingPage */}
      <footer className="py-12" style={{ backgroundColor: '#1E3A4C' }}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="/logo-new.png" 
                alt="Survivor Fantasy League" 
                className="w-10 h-10"
              />
              <div>
                <span className="text-white font-bold text-lg">Survivor Fantasy League</span>
                <p className="text-xs" style={{ color: '#4682B4' }}>The Ultimate Survivor Experience</p>
              </div>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 text-sm" style={{ color: '#F5F0E6' }}>
              <Link to="/" className="hover:text-[#FF7F50] transition-colors">Home</Link>
              <button onClick={() => scrollToSection('features')} className="hover:text-[#FF7F50] transition-colors">Features</button>
              <button onClick={() => scrollToSection('how-it-works')} className="hover:text-[#FF7F50] transition-colors">How It Works</button>
              <button onClick={() => scrollToSection('faq')} className="hover:text-[#FF7F50] transition-colors">FAQ</button>
              <Link to="/login" className="hover:text-[#FF7F50] transition-colors">Login</Link>
            </div>
          </div>
          
          <div className="border-t mt-8 pt-8 text-center text-sm" style={{ borderColor: '#4682B4', color: '#4682B4' }}>
            <p>© {new Date().getFullYear()} Survivor Fantasy League. Not affiliated with CBS or Survivor.</p>
          </div>
        </div>
      </footer>

      {/* Image Lightbox Modal */}
      <ImageLightbox 
        isOpen={lightboxImage.isOpen}
        imageSrc={lightboxImage.src}
        imageAlt={lightboxImage.alt}
        onClose={handleLightboxClose}
      />
    </div>
  );
}
